import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Department, DepartmentNode, DepartmentStatus } from '../../types';
import { departmentsService } from '../../services/api/departments';
import Loading from '../../components/common/Loading';
import ErrorMessage from '../../components/common/ErrorMessage';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import DepartmentFormModal from './DepartmentFormModal';
import { useToast } from '../../contexts/ToastContext';
import { usePermissions, PERMISSIONS } from '../../contexts/PermissionContext';
import './DepartmentList.css';

// Tree node component
interface TreeNodeProps {
  node: DepartmentNode;
  level: number;
  onEdit: (dept: Department) => void;
  onDelete: (dept: Department) => void;
  canEdit: boolean;
  canDelete: boolean;
  expandedNodes: Set<number>;
  toggleExpand: (id: number) => void;
}

const TreeNode: React.FC<TreeNodeProps> = ({
  node,
  level,
  onEdit,
  onDelete,
  canEdit,
  canDelete,
  expandedNodes,
  toggleExpand,
}) => {
  const { t } = useTranslation();
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedNodes.has(node.id);

  return (
    <div className="tree-node">
      <div
        className="tree-node-content"
        style={{ paddingLeft: `${level * 24 + 12}px` }}
      >
        <div className="tree-node-left">
          {hasChildren ? (
            <button
              className="expand-btn"
              onClick={() => toggleExpand(node.id)}
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
            >
              {isExpanded ? '▼' : '▶'}
            </button>
          ) : (
            <span className="expand-placeholder"></span>
          )}
          <div className="tree-node-info">
            <span className="dept-code">{node.code}</span>
            <span className="dept-name">{node.name}</span>
          </div>
        </div>
        <div className="tree-node-right">
          <span className={`status-badge ${node.status.toLowerCase()}`}>
            {t(`status.${node.status.toLowerCase()}`)}
          </span>
          <div className="action-buttons">
            {canEdit && (
              <button
                className="button-link"
                onClick={() => onEdit(node)}
              >
                {t('common.edit')}
              </button>
            )}
            {canDelete && (
              <button
                className="button-link danger"
                onClick={() => onDelete(node)}
              >
                {t('common.delete')}
              </button>
            )}
          </div>
        </div>
      </div>
      {hasChildren && isExpanded && (
        <div className="tree-children">
          {node.children!.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              level={level + 1}
              onEdit={onEdit}
              onDelete={onDelete}
              canEdit={canEdit}
              canDelete={canDelete}
              expandedNodes={expandedNodes}
              toggleExpand={toggleExpand}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const DepartmentList: React.FC = () => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useToast();
  const { hasPermission } = usePermissions();

  // Permissions
  const canCreate = hasPermission(PERMISSIONS.DEPARTMENTS_CREATE);
  const canEdit = hasPermission(PERMISSIONS.DEPARTMENTS_EDIT);
  const canDelete = hasPermission(PERMISSIONS.DEPARTMENTS_DELETE);

  // Data state
  const [departments, setDepartments] = useState<Department[]>([]);
  const [treeData, setTreeData] = useState<DepartmentNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // View mode
  const [viewMode, setViewMode] = useState<'table' | 'tree'>('table');

  // Tree expansion state
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set());

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Modals
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState<Department | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    department: Department | null;
  }>({
    isOpen: false,
    department: null,
  });
  const [deleting, setDeleting] = useState(false);

  // Debounce search
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchDepartments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [flatList, tree] = await Promise.all([
        departmentsService.getAll(),
        departmentsService.getTree(),
      ]);
      setDepartments(flatList);
      setTreeData(tree);

      // Auto-expand root nodes
      const rootIds = tree.map((node) => node.id);
      setExpandedNodes(new Set(rootIds));
    } catch (err: any) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  // Filter departments for table view
  const filteredDepartments = departments.filter((dept) => {
    const matchesSearch =
      !debouncedSearch ||
      dept.code.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      dept.name.toLowerCase().includes(debouncedSearch.toLowerCase());
    const matchesStatus = !statusFilter || dept.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Get parent department name
  const getParentName = (parentId?: number) => {
    if (!parentId) return '-';
    const parent = departments.find((d) => d.id === parentId);
    return parent ? `${parent.code} - ${parent.name}` : `ID: ${parentId}`;
  };

  const handleCreate = () => {
    setEditingDepartment(null);
    setIsFormModalOpen(true);
  };

  const handleEdit = (department: Department) => {
    setEditingDepartment(department);
    setIsFormModalOpen(true);
  };

  const handleDelete = (department: Department) => {
    setDeleteConfirm({ isOpen: true, department });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.department) return;

    setDeleting(true);
    try {
      await departmentsService.delete(deleteConfirm.department.id);
      showSuccess(t('departments.deleteSuccess', 'Department deleted successfully'));
      fetchDepartments();
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || 'Delete failed';
      showError(errorMessage);
    } finally {
      setDeleting(false);
      setDeleteConfirm({ isOpen: false, department: null });
    }
  };

  const toggleExpand = (nodeId: number) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    const getAllIds = (nodes: DepartmentNode[]): number[] => {
      return nodes.flatMap((node) => [
        node.id,
        ...(node.children ? getAllIds(node.children) : []),
      ]);
    };
    setExpandedNodes(new Set(getAllIds(treeData)));
  };

  const collapseAll = () => {
    setExpandedNodes(new Set());
  };

  if (error && departments.length === 0) {
    return <ErrorMessage error={error} onRetry={fetchDepartments} />;
  }

  return (
    <div className="department-list-page">
      <div className="page-header">
        <h1>{t('departments.title')}</h1>
        <div className="header-actions">
          {canCreate && (
            <button className="button button-primary" onClick={handleCreate}>
              + {t('departments.create')}
            </button>
          )}
        </div>
      </div>

      <div className="filters-bar">
        <input
          type="text"
          className="search-input"
          placeholder={t('common.search') + '...'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <select
          className="filter-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">{t('common.status')}: {t('reports.filters.allDepartments', 'All')}</option>
          <option value="ACTIVE">{t('status.active')}</option>
          <option value="INACTIVE">{t('status.inactive')}</option>
        </select>
        <div className="view-toggle">
          <button
            className={`toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
            onClick={() => setViewMode('table')}
          >
            📋 {t('departments.tableView', 'Table')}
          </button>
          <button
            className={`toggle-btn ${viewMode === 'tree' ? 'active' : ''}`}
            onClick={() => setViewMode('tree')}
          >
            🌳 {t('departments.treeView', 'Tree')}
          </button>
        </div>
        <span className="results-count">
          {filteredDepartments.length} {t('departments.title').toLowerCase()}
        </span>
      </div>

      {loading && departments.length === 0 ? (
        <Loading text={t('common.loading')} />
      ) : departments.length === 0 ? (
        <div className="empty-state">
          <p>{t('common.noData')}</p>
          {canCreate && (
            <button className="button button-primary" onClick={handleCreate}>
              {t('departments.create')}
            </button>
          )}
        </div>
      ) : viewMode === 'table' ? (
        <div className="departments-table">
          <table>
            <thead>
              <tr>
                <th>{t('departments.code')}</th>
                <th>{t('departments.name')}</th>
                <th>{t('departments.parent')}</th>
                <th>{t('departments.status')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredDepartments.map((department) => (
                <tr key={department.id}>
                  <td className="code-cell">{department.code}</td>
                  <td>{department.name}</td>
                  <td className="parent-cell">{getParentName(department.parentId)}</td>
                  <td>
                    <span
                      className={`status-badge ${department.status.toLowerCase()}`}
                    >
                      {t(`status.${department.status.toLowerCase()}`)}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      {canEdit && (
                        <button
                          className="button-link"
                          onClick={() => handleEdit(department)}
                        >
                          {t('common.edit')}
                        </button>
                      )}
                      {canDelete && (
                        <button
                          className="button-link danger"
                          onClick={() => handleDelete(department)}
                        >
                          {t('common.delete')}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="departments-tree">
          <div className="tree-controls">
            <button className="button button-small" onClick={expandAll}>
              {t('departments.expandAll', 'Expand All')}
            </button>
            <button className="button button-small" onClick={collapseAll}>
              {t('departments.collapseAll', 'Collapse All')}
            </button>
          </div>
          <div className="tree-container">
            {treeData.length === 0 ? (
              <div className="empty-state">
                <p>{t('common.noData')}</p>
              </div>
            ) : (
              treeData.map((node) => (
                <TreeNode
                  key={node.id}
                  node={node}
                  level={0}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  canEdit={canEdit}
                  canDelete={canDelete}
                  expandedNodes={expandedNodes}
                  toggleExpand={toggleExpand}
                />
              ))
            )}
          </div>
        </div>
      )}

      {/* Form Modal */}
      <DepartmentFormModal
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false);
          setEditingDepartment(null);
        }}
        onSuccess={fetchDepartments}
        department={editingDepartment}
        departments={departments}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, department: null })}
        onConfirm={confirmDelete}
        title={t('common.delete') + ' ' + t('departments.title')}
        message={t(
          'departments.deleteConfirmMessage',
          `Are you sure you want to delete department "${deleteConfirm.department?.name}"? This action cannot be undone. Departments with sub-departments cannot be deleted.`
        )}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        variant="danger"
        isLoading={deleting}
      />
    </div>
  );
};

export default DepartmentList;
